import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import {
  filterWellsFromList,
  setIsMidstreamNavigatorFilter,
  setWellListExtent
} from "store/features";
import {
  filterWellsFromList as filterFacilitiesFromList,
  setWellListExtent as setFacilityListExtent,
  setIsMidstreamNavigatorFilter as setIsMidstreamNavigatorFacilityFilter
} from "store/features/filter/facilityFilterSlice";
// eslint-disable-next-line no-restricted-imports -- intentional dual-write for cross-tab redux-state-sync; see EVA-6684
import { setSelectedWells as setSelectedWellsRedux } from "store/features/map/mapSlice";
import { selectSelectedFacilities, selectSelectedWells, useMapStore } from "stores/map";

import { EntityKind } from "models/entityKind";

import useGetFacilityNetworkFraction from "components/map/hooks/useGetFacilityNetworkFraction";

import { useFilterUndo } from "./useFilterUndo";

/**
 * Hook for filtering to a selected facility and its contributing wells.
 * Now uses map selection state instead of WellListProvider context.
 */
export const useFacilityAndWellsFilter = () => {
  const dispatch = useDispatch();
  const { undoFilters } = useFilterUndo();
  // Get selected entities from map state instead of WellListProvider
  const selectedWells = useMapStore(selectSelectedWells);
  const selectedFacilities = useMapStore(selectSelectedFacilities);
  const setSelectedEntities = useMapStore((s) => s.setSelectedEntities);

  // Get the first selected facility (prioritize direct facility selections)
  const selectedFacilityUwi = (() => {
    const facilityUwis = Object.keys(selectedFacilities);
    if (facilityUwis.length > 0) {
      return facilityUwis[0];
    }

    // Fallback to wells if no direct facility selection
    const wellUwis = Object.keys(selectedWells);
    return wellUwis.length > 0 ? wellUwis[0] : null;
  })();

  // Get network data for the selected facility
  const { data: networkFractions, error } = useGetFacilityNetworkFraction(
    selectedFacilityUwi || ""
  );

  const filterToFacilityAndContributingWells = useCallback(() => {
    if (!selectedFacilityUwi) {
      toast.warn("Please select a facility first");
      return;
    }

    if (error || !networkFractions) {
      toast.error("Could not get facility network data");
      return;
    }

    // Extract well IDs from network fractions
    const wellIds = networkFractions.map((fraction) => fraction.wellId);
    if (wellIds.length === 0) {
      toast.warn("No contributing wells found for selected facility.");
      return;
    }

    // Filter wells first, then facilities (correct order)
    dispatch(setIsMidstreamNavigatorFilter(true));
    dispatch(setIsMidstreamNavigatorFacilityFilter(true));

    dispatch(filterWellsFromList({ wellList: wellIds, resetMidstreamFlag: false }));

    dispatch(
      filterFacilitiesFromList({
        wellList: [selectedFacilityUwi],
        resetMidstreamFlag: false
      })
    );

    // The well filter is about to become this facility's contributing wells. A
    // well selected before this is not one of them, so it is about to leave the
    // map — and a selection the filter excludes is what makes the Network Table
    // report an entity as "not within map view" while everything on screen
    // belongs to the facility the user just filtered to.
    //
    // Only prune when a facility was actually selected. With none, the anchor
    // above falls back to a selected well, and a well is not among its own
    // contributing wells — pruning would clear the very selection driving the
    // filter. The toolbar disables this button with no facility selected, so
    // that state is unreachable today; the guard keeps the hook correct on its
    // own terms rather than on its caller's.
    //
    // `wellId` and the map selection key are both the unseparated form; the
    // tests pin that with ids taken from real responses, because a separator
    // mismatch would silently prune every selected well.
    if (Object.keys(selectedFacilities).length > 0) {
      const contributing = new Set(wellIds);
      const keptWells = Object.fromEntries(
        Object.entries(selectedWells).filter(([uwi]) => contributing.has(uwi))
      );
      if (Object.keys(keptWells).length !== Object.keys(selectedWells).length) {
        setSelectedEntities(EntityKind.Well, keptWells);
        // Selection is mirrored into Redux for cross-tab sync, and that mirror is
        // what drives a popped-out dashboard. Without this the popout keeps
        // charting the well the main tab just dropped.
        dispatch(setSelectedWellsRedux(keptWells));
      }
    }
  }, [
    dispatch,
    selectedFacilityUwi,
    error,
    networkFractions,
    selectedWells,
    selectedFacilities,
    setSelectedEntities
  ]);
  const removeFilterToFacilityAndContributingWells = useCallback(
    async (isToggled: boolean = false) => {
      if (isToggled) {
        dispatch(undoFilters());
      } else {
        dispatch(setFacilityListExtent(undefined));
        dispatch(setWellListExtent(undefined));
      }
    },
    [dispatch]
  );
  return {
    filterToFacilityAndContributingWells,
    removeFilterToFacilityAndContributingWells,
    networkFractions,
    selectedFacilityUwi // Expose selected facility for analytics
  };
};
